import { HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  EmailAutoSaveSubscriptionCount,
  EmailsExclusionListResponse,
  EmailSubscription,
  EmailSubscriptionBulkResponse,
  EmailSubscriptionConfiguration,
  EmailSubscriptionsPaginatedResponse,
  HeartbeatPayload,
  OutlookSyncEntityType,
} from '../models';
import { OutlookSyncHttpBase } from './outlook-sync-http-base';

@Injectable()
export class EmailSyncService extends OutlookSyncHttpBase {
  getEmailAutoSaveSubscriptionCount(
    syncId: string,
  ): Observable<EmailAutoSaveSubscriptionCount> {
    return this.http.get<EmailAutoSaveSubscriptionCount>(
      `${this.syncConfigBasePath}/${syncId}/email-subscriptions/count`,
      {
        headers: this.buildHeaders(),
      },
    );
  }

  insertBulkEmailAutoSaveSubscription(
    syncId: string,
    subscriptions: EmailSubscription[],
  ): Observable<EmailSubscriptionBulkResponse> {
    const requestObj = {
      Id: syncId,
      Subscriptions: subscriptions,
    };
    return this.http
      .put<EmailSubscriptionBulkResponse>(
        `${this.syncConfigBasePath}/${syncId}/email-subscriptions/bulk`,
        requestObj,
        {
          headers: this.buildHeaders(),
        },
      )
      .pipe(
        map((r) => ({
          totalCount: r.totalCount ?? 0,
          createdCount: r.createdCount ?? 0,
          updatedCount: r.updatedCount ?? 0,
          failedCreations: r.failedCreations ?? 0,
          failedUpdates: r.failedUpdates ?? 0,
        })),
      );
  }

  updateEmailAutoSaveSubscription(
    syncId: string,
    subscription: EmailSubscription,
  ): Observable<boolean> {
    return this.http
      .put<void>(
        `${this.syncConfigBasePath}/${syncId}/email-subscriptions`,
        subscription,
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(this.mapStatusOk({ allowed: [HttpStatusCode.Accepted] }));
  }

  getEmailSubscriptionByKey(
    maximizerKey: string,
    syncId: string,
  ): Observable<EmailSubscriptionConfiguration | undefined> {
    const encodedKey = encodeURIComponent(maximizerKey);
    return this.http.get<EmailSubscriptionConfiguration | undefined>(
      `${this.syncConfigBasePath}/${syncId}/email-subscriptions/${encodedKey}?allowEmpty=true`,
      {
        headers: this.buildHeaders({ ignoreInsights: true }),
      },
    );
  }

  upsertHeartbeat(
    syncConfigId: string,
    payload: HeartbeatPayload,
  ): Observable<string | null> {
    return this.http
      .post<{ heartbeatId: string }>(
        `${this.syncConfigBasePath}/${syncConfigId}/emails/heartbeat`,
        payload,
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(
        map((response) => {
          const id = response.body?.heartbeatId;
          return typeof id === 'string' && id.length > 0 ? id : null;
        }),
        // Let transport failures propagate so callers can distinguish them from an
        // empty (null) body and retry; null now means only "success, no id returned".
      );
  }

  deleteHeartbeat(
    syncConfigId: string,
    heartbeatId: string,
  ): Observable<boolean> {
    const encodedHeartbeatId = encodeURIComponent(heartbeatId);
    return this.http
      .delete<void>(
        `${this.syncConfigBasePath}/${syncConfigId}/emails/heartbeat/${encodedHeartbeatId}`,
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(
        this.mapStatusOk({
          allowed: [HttpStatusCode.NoContent],
          catchAs: false,
        }),
      );
  }

  getEmailExclusionList(
    syncId: string,
  ): Observable<EmailsExclusionListResponse> {
    return this.http.get<EmailsExclusionListResponse>(
      `${this.syncConfigBasePath}/${syncId}/exclusion-lists/emails`,
      {
        headers: this.buildHeaders(),
      },
    );
  }

  getEmailSubscriptions(
    syncId: string,
    options: {
      pageSize?: number;
      offset?: number;
      onlyEnabled?: boolean;
    } = {},
  ): Observable<EmailSubscriptionsPaginatedResponse> {
    let params = new HttpParams();

    if (options.pageSize !== undefined) {
      params = params.set('PageSize', options.pageSize.toString());
    }

    if (options.offset !== undefined) {
      params = params.set('Offset', options.offset.toString());
    }

    if (options.onlyEnabled !== undefined) {
      params = params.set('OnlyEnabled', String(options.onlyEnabled));
    }

    return this.http.get<EmailSubscriptionsPaginatedResponse>(
      `${this.syncConfigBasePath}/${syncId}/email-subscriptions`,
      {
        headers: this.buildHeaders(),
        params,
      },
    );
  }

  addToEmailExclusionList(
    syncId: string,
    maximizerKey: string,
    maximizerEntityType: OutlookSyncEntityType,
  ): Observable<boolean> {
    return this.http
      .post<void>(
        `${this.syncConfigBasePath}/${syncId}/exclusion-lists/emails`,
        {
          maximizerKey,
          maximizerEntityType,
        },
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(this.mapStatusOk());
  }

  removeFromEmailExclusionList(
    syncId: string,
    maximizerKey: string,
    maximizerEntityType: OutlookSyncEntityType,
  ): Observable<boolean> {
    const encodedKey = encodeURIComponent(maximizerKey);
    return this.http
      .delete<void>(
        `${this.syncConfigBasePath}/${syncId}/exclusion-lists/emails/${encodedKey}?maximizerEntityType=${maximizerEntityType}`,
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(this.mapStatusOk());
  }

  deleteEmailSubscription(
    syncId: string,
    maximizerKey: string,
  ): Observable<boolean> {
    const encodedKey = encodeURIComponent(maximizerKey);
    return this.http
      .delete<void>(
        `${this.syncConfigBasePath}/${syncId}/email-subscriptions/${encodedKey}`,
        {
          headers: this.buildHeaders(),
          observe: 'response',
        },
      )
      .pipe(
        this.mapStatusOk({
          allowed: [HttpStatusCode.Ok, HttpStatusCode.NoContent],
        }),
      );
  }
}
